import getCDNImagePath from '@ui/Utils/getCDNImagePath';

export type GainMode = 'slow_attack' | 'manual';

export type DeviceMode = 'sweep' | 'center';

export interface DeviceStatus {
    [k: string]: unknown;
    act_mode?: DeviceMode;
    ant_comp?: number | string;
    ant1_pts?: number | string;
    ant1_state?: number | string;
    ant2_pts?: number | string;
    ant2_state?: number | string;
    bl_running?: number | string;
    bl_sweep_n?: number | string;
    bl_sweeps_n?: number | string;
    bl_target_n?: number | string;
    bl_valid?: number | string;
    center?: Partial<CenterDeviceRuntimeStatus>;
    cfg_mode?: DeviceMode;
    device_id?: string;
    enabled?: number | string;
    errs?: number | string;
    id?: string;
    is_alert?: number | string;
    live?: number | string;
    raw?: string;
    rf_temp_c?: number | string;
    rows?: number | string;
    save?: number | string;
    save_target_bins?: number | string;
    start?: number | string;
    step?: number | string;
    stop?: number | string;
    version?: string;
}

export interface WsDeviceInfo {
    [k: string]: unknown;
    act_mode?: DeviceMode;
    ant_comp?: number;
    ant1_pts?: number;
    ant1_state?: number;
    ant2_pts?: number;
    ant2_state?: number;
    baseline_port?: number;
    bl_running?: number;
    bl_sweep_n?: number;
    bl_target_n?: number;
    bl_valid?: number;
    center_hz?: number;
    center_span_hz?: number;
    cfg_mode?: DeviceMode;
    connected_for_s?: number;
    control_port?: number;
    dc_block?: number;
    enabled?: number;
    errs?: number;
    fft_avg_applied?: number;
    id: string;
    ip?: string;
    iq_stream_enabled?: number;
    is_alert?: 0 | 1 | 2;
    last_hb_ts?: number;
    last_ns?: number;
    live?: number;
    live_port?: number;
    noise_figure?: number;
    online?: boolean;
    online_since_ts?: number;
    restart_required?: number;
    rows?: number;
    save?: number;
    save_stop_at_ts?: number;
    start?: number;
    step?: number;
    stop?: number;
    temp_used?: number;
    ts?: number;
    vbins?: number;
    version?: string;
    window?: number;
    x_pct?: number;
    y_pct?: number;
}

export type AnalyzerRuntimeSource = 'status' | 'device' | 'unavailable';

export interface AnalyzerRuntimeState {
    readonly deviceId: string;
    readonly deviceInfo: WsDeviceInfo | null;
    readonly dynamic: {
        readonly alertLevel: 0 | 1 | 2 | null;
        readonly antennaCompensationEnabled: boolean | null;
        readonly antennaOnePoints: number | null;
        readonly antennaOneState: number | null;
        readonly antennaTwoPoints: number | null;
        readonly antennaTwoState: number | null;
        readonly baselineRunning: boolean | null;
        readonly baselineSweepCount: number | null;
        readonly baselineTargetCount: number | null;
        readonly baselineValid: boolean | null;
        readonly enabled: boolean | null;
        readonly errors: number | null;
        readonly live: boolean | null;
        readonly rfTemperatureC: number | null;
        readonly rows: number | null;
        readonly save: boolean | null;
        readonly saveTargetBins: number | null;
        readonly startHz: number | null;
        readonly stepHz: number | null;
        readonly stopHz: number | null;
    };
    readonly dynamicSource: AnalyzerRuntimeSource;
    readonly metadata: {
        readonly baselinePort: number | null;
        readonly connectedForSeconds: number | null;
        readonly controlPort: number | null;
        readonly heartbeatTs: number | null;
        readonly ip: string | null;
        readonly lastSweepNs: number | null;
        readonly livePort: number | null;
        readonly online: boolean | null;
        readonly onlineSinceTs: number | null;
        readonly saveStopAtTs: number | null;
        readonly version: string | null;
    };
    readonly metadataSource: AnalyzerRuntimeSource;
    readonly status: DeviceStatus | null;
    readonly subscribed: boolean;
    readonly wsConnected: boolean;
}

interface AlertMessage {
    bin: number;
    diff_db: number;
    freq_hz: number;
    severity: 1 | 2;
}

export interface LiveRowPayload {
    alerts_rx1?: AlertMessage[];
    alerts_rx2?: AlertMessage[];
    bins: number;
    device_id: string;
    f_start_hz: number;
    f_step_hz: number;
    rx1_dbfs: number[];
    rx2_dbfs: number[];
    t_ns: number;
    type: 'live_row';
}

export interface BaselinesPayload {
    bins: number;
    completed_ns: number;
    device_id: string;
    f_start_hz: number;
    f_step_hz: number;
    f_stop_hz: number;
    rx1: number[];
    rx1_ant: string;
    rx2: number[];
    rx2_ant: string;
    sdr_at_capture: {
        ant_comp_applied: boolean;
        gain_mode: GainMode;
        rf_bandwidth_mhz: number;
        rx1_gain_db: number;
        rx2_gain_db: number;
        sample_rate_mhz: number;
        samples_per_step: number;
    };
    source_baseline_file?: string | null;
    sweeps_done: number;
    thresholds?: {
        alert_streak: number;
        th_pct_high: number;
        th_pct_low: number;
    };
}

export interface DeviceConfig {
    alert_streak_th?: number;
    f_start_mhz: number;
    f_step_mhz: number;
    f_stop_mhz: number;
    gain_mode: GainMode;
    rf_bandwidth_mhz: number;
    rx1_antenna_id?: string;
    rx1_gain_db: number;
    rx2_antenna_id?: string;
    rx2_gain_db: number;
    sample_rate_mhz: number;
    samples_per_step: number;
    samples_skip_ms?: number;
    save_target_bins?: number;
    threshold_high_pct?: number;
    threshold_low_pct?: number;
}

export interface CenterSharedConfig {
    gain_mode: GainMode;
    rf_bandwidth_mhz: number;
    rx1_gain_db: number;
    rx2_gain_db: number;
}

export interface CenterDeviceConfigValues {
    center_freq_mhz: number;
    center_sample_rate_mhz: number;
    dc_block_enabled: boolean;
    fft_averages: number;
    fft_size: number;
    row_interval_ms: number;
    window_enabled: boolean;
}

export interface CenterDeviceRuntimeStatus {
    center_hz: number;
    center_span_hz: number;
    dc_block: number;
    e_rf_bandwidth_hz: number;
    e_sample_rate_hz: number;
    fft_avg_applied: number;
    interval_ms: number;
    view_bins: number;
    window: number;
}

export interface CenterDeviceConfig {
    center: CenterDeviceConfigValues;
    device_id: string;
    mode: 'center';
    shared: CenterSharedConfig;
}

export interface CenterDeviceStatus {
    act_mode: 'center';
    center: Partial<CenterDeviceRuntimeStatus>;
    cfg_mode?: DeviceMode;
    device_id: string;
    enabled: number | string;
    errs: number | string;
    is_alert?: number | string;
    live: number | string;
    rows: number | string;
    version?: string;
}

export interface CenterLiveRowPayload {
    bin_width_hz: number;
    bins: number;
    center_hz: number;
    device_id: string;
    rx1_dbfs: number[];
    rx2_dbfs?: number[];
    span_hz: number;
    t_ns: number;
    type: 'live_row';
}

export interface CenterRendererFrame {
    bins: number;
    centerHz: number;
    rx1Dbfs: Float32Array;
    rx2Dbfs: Float32Array | null;
    spanHz: number;
    startHz: number;
    stepHz: number;
    timestampNs: number;
}

export interface CenterWaterfallRow {
    timestampNs: number;
    values: Float32Array;
}

export interface CenterWaterfallChannels {
    rx1: CenterWaterfallRow[];
    rx2: CenterWaterfallRow[];
}

export interface CenterRendererState {
    latestFrame: CenterRendererFrame | null;
    waterfallRows: CenterWaterfallChannels;
}

export interface SdrWsPanelBaseProps {
    analyzerRuntime?: AnalyzerRuntimeState | null;
    config?: DeviceConfig | null;
    configRef?: React.RefObject<DeviceConfig | null>;
    connectWs: () => void;
    device?: WsDeviceInfo | null;
    deviceId?: string | null;
    deviceStatus?: DeviceStatus | null;
    disconnectWs: () => void;
    fetchBaseline?: () => Promise<unknown>;
    fetchConfig?: () => Promise<DeviceConfig | null>;
    fetchDevice?: () => Promise<unknown>;
    fetchStatus?: () => Promise<unknown>;
    getLogs?: () => string[];
    loadMockData?: (path?: string) => Promise<void> | void;
    logs?: string[];
    patchConfig?: (update: Partial<DeviceConfig>) => Promise<DeviceConfig | null>;
    postLiveStart?: () => Promise<unknown>;
    postLiveStop?: () => Promise<unknown>;
    postSubscribeStart?: () => Promise<unknown>;
    postSubscribeStop?: () => Promise<unknown>;
    postSweepStart?: () => Promise<unknown>;
    postSweepStop?: () => Promise<unknown>;
    pushLog?: (m: string) => void;
    resetBuffers?: () => void;
    setTestStartMHz: (n: number) => void;
    setTestStepMHz: (n: number) => void;
    setTestStopMHz: (n: number) => void;
    subscribed?: boolean;
    testSessionToken?: string | null;
    testStartMHz: number;
    testStepMHz: number;
    testStopMHz: number;
    testWsState: string;
}

export interface PanelProps extends SdrWsPanelBaseProps {
    alerts?: AlertItem[];
    baselineDoneCounter?: number;
    baselineList?: {
        cfg?: { binWidthMHz: number };
        timestamp: number;
        valuesRx1?: { max?: ArrayLike<number> };
    }[];
    baselineRowCount?: number;
    mockRunning?: boolean;
    passes?: number;
    postAlertTrackStart?: (baselineTimestamps?: number[]) => void;
    postAlertTrackStop?: () => void;
    postBaselineStart?: (rowsTarget?: number) => Promise<unknown>;
    postBaselineStartLocal?: (rowsTarget?: number) => void;
    postBaselineStop?: () => Promise<unknown>;
    postBaselineStopLocal?: () => void;
    resetBuffers?: () => void;
    rx1ThresholdHighDb?: number;
    rx1ThresholdMediumDb?: number;
    rx2ThresholdHighDb?: number;
    rx2ThresholdMediumDb?: number;
    selectedBaselineTs?: number | null;
    sessionRowCount?: number;
    setPasses?: (n: number) => void;
    setSelectedBaselineTs?: (ts: number | null) => void;
    stopMock?: () => void;
}

export interface AlertItem {
    id: string;
    message: string;
    severity?: string;
    time?: string;
}

export interface RunningStats {
    avg: Float32Array;
    count: number;
    peak: Float32Array;
}

export interface ArchiveSweepFile {
    filename: string;
    size_bytes?: number;
}

export interface ArchiveSweepListPayload {
    dir: string;
    files: ArchiveSweepFile[];
}

export interface AntennaManifestItem {
    [k: string]: unknown;
    antenna_id: string;
    band_id: string;
    count: number;
    dataset_id?: string;
    id?: string;
    is_interp_extrap?: boolean;
    mtime: number;
    sha_bin: string;
    sha_json: string;
    url_bin: string;
    url_json: string;
}

export interface AntennaManifest {
    items: AntennaManifestItem[];
    version: number;
}

export interface AntennaCorrectionStats {
    max: number;
    mean: number;
    min: number;
    n: number;
    nonzero_pct: number;
    p50: number;
    p95: number;
}

export interface AntennaNoiseFigureSegment {
    count: number;
    id: string;
    nf_cdb: number[];
    start_hz: number;
    step_hz: number;
}

export interface AntennaData {
    [k: string]: unknown;
    corr_cdb: number[];
    corr_stats: AntennaCorrectionStats;
    count: number;
    f0_hz: number;
    freqs_hz: number[];
    id: string;
    is_interp_extrap: boolean;
    nf_ad9361_segments: AntennaNoiseFigureSegment[];
    step_hz: number;
    vswr: number[];
}

export interface Placement {
    deviceId: string;
    id: string;
    xPct: number;
    yPct: number;
}

export interface LayoutCoordDTO {
    device_id: string;
    x_pct: number;
    y_pct: number;
}

export interface LayoutDTO {
    coords: LayoutCoordDTO[];
    reference: string;
}

export interface LayoutResponse {
    last_modified: string;
    layout: LayoutDTO;
}

export const STORAGE_KEY = 'sweep_locations_placements_v1';
export const CALIBRATION_KEY = 'sweep_locations_calibration_v1';
export const MEASUREMENTS_KEY = 'sweep_locations_measurements_v1';
export const FLOORPLAN_SRC = getCDNImagePath('locations/Nokia_Arena/Floorplans/Nokia_Arena_Arena_Level.png');

export type SweepRow = number[];

export type SweepRows = SweepRow[];

export interface SweepDrawConfig {
    binWidthMHz: number;
    freqEndMHz: number;
    freqStartMHz: number;
}

export interface CanvasMetrics {
    cssH: number;
    cssW: number;
    devH: number;
    devW: number;
}

export interface CanvasMetricsSet {
    axis: CanvasMetrics | null;
    spectrum: CanvasMetrics | null;
    waterfall: CanvasMetrics | null;
}

export interface CursorState {
    xContent: number | null;
}

export type AlertSeverity = 'low' | 'medium' | 'high';

export interface AlertMarker {
    db: number;
    freqMHz: number;
    // Index into the sweep rows array (sweepRowsRef/rx2RowsRef) at the time this alert fired.
    // Used by the waterfall renderer to anchor the marker to its data row so it scrolls with the
    // waterfall and disappears once that row scrolls out of the visible viewport.
    rowIndex?: number;
    severity?: AlertSeverity;
    ts: number;
}

export interface TracePalette {
    average: string;
    latest: string;
    peak: string;
}

export interface ThresholdColors {
    high: string;
    medium: string;
}
